import {Button} from '@dropbox/dig-components/buttons';
import {Modal} from '@dropbox/dig-components/modal';
import {Select} from '@dropbox/dig-components/text_fields';
import {Box} from '@dropbox/dig-foundations';
import {EditorDocAdmin, PlanningCycle} from 'client';
import {CancelButton} from 'components/DSYS/CancelButton';
import {Title} from 'components/DSYS/Title';
import {CURRENT_YEAR} from 'constant';
import {t} from 'i18next';
import {useState} from 'react';
import {queryClient} from 'views/QueryClientWrapper';

import {usePlanningDocService} from './hooks';

const PLANNING_YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR + i);

export const PlanningCycleModal = ({
  open,
  onClose,
  planningId,
  planningCycle,
  planningYear,
}: {
  open: boolean;
  onClose: () => void;
  planningId: string;
  planningCycle: PlanningCycle | undefined;
  planningYear: number | undefined;
}) => {
  const {updatePlanningDoc} = usePlanningDocService();

  const [isPending, setIsPending] = useState(false);
  const [selectedPlanningCycle, setSelectedPlanningCycle] = useState<string | undefined>(
    planningCycle
  );
  const [selectedPlanningYear, setSelectedPlanningYear] = useState<string | undefined>(
    planningYear ? String(planningYear) : undefined
  );

  const handleSave = async () => {
    if (!selectedPlanningCycle || !selectedPlanningYear) return;
    setIsPending(true);
    const result = await updatePlanningDoc({
      planningId: planningId,
      update: {
        planning_cycle: selectedPlanningCycle as PlanningCycle,
        planning_year: parseInt(selectedPlanningYear),
      },
    });
    setIsPending(false);
    onClose();

    queryClient.setQueryData(['planning', 'documents', 'all'], (old: Array<EditorDocAdmin>) => {
      return old.map((doc) => {
        if (doc.id === planningId) {
          return {
            ...doc,
            planning_cycle: result.planning_cycle,
            planning_year: result.planning_year,
          };
        }
        return doc;
      });
    });
  };

  const handleCloseModal = () => {
    onClose();
    setSelectedPlanningCycle(planningCycle);
    setSelectedPlanningYear(String(planningYear));
  };

  return (
    <Modal
      open={open}
      withCloseButton="close"
      width="small"
      aria-label="Edit planning cycle"
      onRequestClose={handleCloseModal}
    >
      <Modal.Header>
        <Title>{t('edit_planning_cycle')}</Title>
      </Modal.Header>
      <Modal.Body>
        <Box display="flex" flexDirection="row" style={{gap: 8}}>
          <Select
            id="planning-cycle-select"
            aria-label="Planning cycle"
            size="large"
            value={selectedPlanningCycle}
            onChange={setSelectedPlanningCycle}
          >
            {Object.values(PlanningCycle).map((cycle) => (
              <Select.Option key={`planning-cycle__${cycle}`} value={cycle}>
                {cycle}
              </Select.Option>
            ))}
          </Select>
          <Select
            id="planning-year-select"
            aria-label="Planning year"
            size="large"
            value={selectedPlanningYear}
            onChange={setSelectedPlanningYear}
          >
            {PLANNING_YEARS.map((year) => (
              <Select.Option key={`planning-year__${year}`} value={String(year)}>
                {String(year)}
              </Select.Option>
            ))}
          </Select>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <CancelButton onClick={handleCloseModal} disabled={isPending}>
          {t('cancel')}
        </CancelButton>
        <Button
          variant="primary"
          onClick={handleSave}
          isLoading={isPending}
          disabled={!selectedPlanningCycle || !selectedPlanningYear}
        >
          {t('save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
